import React, { useEffect, useState } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { useTenant } from '../contexts/TenantContext'

export default function Header() {
  const { tenant } = useTenant()
  const nome = tenant?.nome || ''
  const [isMobile, setIsMobile] = useState(false)
  const [menuAberto, setMenuAberto] = useState(false)

  // Deteta mobile (<=768px) para alternar entre links e hambúrguer
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const atualizar = () => setIsMobile(mq.matches)
    atualizar()
    mq.addEventListener('change', atualizar)
    return () => mq.removeEventListener('change', atualizar)
  }, [])

  // Bloqueia scroll do body + fecha com Esc enquanto o overlay está aberto
  useEffect(() => {
    if (!menuAberto) return
    document.body.classList.add('menu-aberto')
    const onKey = (e) => { if (e.key === 'Escape') setMenuAberto(false) }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.classList.remove('menu-aberto')
      window.removeEventListener('keydown', onKey)
    }
  }, [menuAberto])

  // Fecha o menu se sair de mobile (ex.: rodar para desktop)
  useEffect(() => {
    if (!isMobile) setMenuAberto(false)
  }, [isMobile])

  const fechar = () => setMenuAberto(false)

  return (
    <header style={{
      background: 'var(--cor-branco)',
      borderBottom: '1px solid var(--cor-borda)',
      padding: '0 clamp(16px, 4vw, 24px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '60px',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: 'var(--sombra)',
    }}>
      <Link to="/" onClick={fechar} style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
        <img
          src="/fotos/Logo.png"
          alt={nome || 'Logo'}
          style={{
            height: 'clamp(32px, 5vw, 44px)',
            width: 'auto',
            display: 'block',
          }}
        />
      </Link>

      {/* Desktop: links inline */}
      {!isMobile && (
        <nav style={{ display: 'flex', gap: '2px' }}>
          <NavLink to="/" end style={navStyle}>Início</NavLink>
          <NavLink to="/agendar" style={navAgendarStyle}>Agendar</NavLink>
        </nav>
      )}

      {/* Mobile: botão hambúrguer */}
      {isMobile && (
        <button
          onClick={() => setMenuAberto(v => !v)}
          aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
          aria-expanded={menuAberto}
          style={{
            width: '44px',
            height: '44px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            background: 'transparent',
            border: 'none',
            padding: 0,
          }}
        >
          {[0, 1, 2].map(i => (
            <span key={i} style={{
              display: 'block',
              width: '24px',
              height: '2px',
              borderRadius: '2px',
              background: 'var(--cor-principal)',
              transition: 'transform 0.25s var(--ease), opacity 0.25s var(--ease)',
              transform:
                menuAberto && i === 0 ? 'translateY(7px) rotate(45deg)' :
                menuAberto && i === 2 ? 'translateY(-7px) rotate(-45deg)' : 'none',
              opacity: menuAberto && i === 1 ? 0 : 1,
            }} />
          ))}
        </button>
      )}

      {/* Overlay do menu mobile */}
      {isMobile && menuAberto && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navegação"
          style={{
            position: 'fixed',
            inset: 0,
            top: '60px',
            zIndex: 90,
            background: 'var(--cor-creme)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '20px',
            padding: '40px 24px',
            animation: 'menuFadeIn 0.2s var(--ease)',
          }}
        >
          <NavLink to="/" end onClick={fechar} style={mobLinkStyle(0)}>Início</NavLink>
          <NavLink to="/agendar" onClick={fechar} style={mobAgendarStyle(1)}>Agendar</NavLink>

          <p style={{
            position: 'absolute',
            bottom: 'max(40px, env(safe-area-inset-bottom))',
            fontFamily: 'var(--font-ui)',
            fontSize: '11px',
            fontWeight: '500',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: 'var(--cor-texto-suave)',
            textAlign: 'center',
          }}>
            Clínica de Cuidado Integrado
          </p>
        </div>
      )}
    </header>
  )
}

function navStyle({ isActive }) {
  return {
    textDecoration: 'none',
    color: isActive ? 'var(--cor-principal)' : 'var(--cor-texto-suave)',
    fontFamily: 'var(--font-ui)',
    fontSize: '14px',
    padding: '8px 12px',
    borderRadius: 'var(--raio)',
    background: isActive ? 'var(--cor-creme)' : 'transparent',
    fontWeight: isActive ? '600' : '400',
    transition: 'all 0.15s',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
  }
}

function navAgendarStyle({ isActive }) {
  return {
    textDecoration: 'none',
    fontFamily: 'var(--font-ui)',
    fontSize: '14px',
    padding: '8px 16px',
    borderRadius: 'var(--raio)',
    fontWeight: '600',
    transition: 'all 0.15s',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    background: isActive ? 'var(--cor-principal-escuro)' : 'var(--cor-principal)',
    color: 'white',
    border: 'none',
  }
}

function mobLinkStyle(ordem) {
  return ({ isActive }) => ({
    textDecoration: 'none',
    fontFamily: 'var(--font-base)',
    fontSize: '32px',
    fontWeight: '400',
    color: isActive ? 'var(--cor-principal)' : 'var(--cor-texto)',
    padding: '8px 16px',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    animation: `menuLinkUp 0.35s var(--ease) ${0.05 + ordem * 0.07}s both`,
  })
}

function mobAgendarStyle(ordem) {
  return () => ({
    textDecoration: 'none',
    fontFamily: 'var(--font-ui)',
    fontSize: '16px',
    fontWeight: '600',
    letterSpacing: '0.03em',
    color: 'white',
    background: 'var(--cor-principal)',
    padding: '14px 40px',
    borderRadius: '50px',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 4px 18px rgba(139, 74, 47, 0.25)',
    animation: `menuLinkUp 0.35s var(--ease) ${0.05 + ordem * 0.07}s both`,
  })
}
